/**
 * English — the alternate language.
 *
 * Mirrors the keys in `sq.js`, which is the reference dictionary. A key missing
 * here falls back to the Albanian string rather than rendering blank.
 */
const en = {
  /* ── sidebar / nav ─────────────────────────────────────────────────── */
  'nav.overview': 'Overview',
  'nav.inbox': 'Action inbox',
  'nav.monthly': 'Monthly registration',
  'nav.collect': 'Data collection',
  'nav.s1': 'Scope 1 — direct',
  'nav.s2': 'Scope 2 — purchased energy',
  'nav.s3': 'Scope 3 — value chain',
  'nav.stationary': 'Stationary combustion',
  'nav.mobile': 'Mobile combustion',
  'nav.process': 'Process emissions',
  'nav.electricity': 'Electricity bills',
  'nav.heat': 'Heating & cooling',
  'nav.travel': 'Travel & commuting',
  'nav.records': 'Records',
  'nav.facilities': 'Facilities',
  'nav.explorer': 'Emissions explorer',
  'nav.quality': 'Data quality',
  'nav.reports': 'Reports',
  'nav.reportsDetail': 'Detailed report',
  'nav.group.collect': 'Collect',
  'nav.group.measure': 'Measure',
  'nav.group.report': 'Report',
  'nav.group.cbam': 'CBAM',
  'nav.cbam': 'CBAM overview',
  'nav.cbamInstallations': 'Installations',
  'nav.cbamGoods': 'Goods & precursors',
  'nav.admin.users': 'Users & plans',
  'nav.admin.tokens': 'Registration tokens',

  /* ── chrome ────────────────────────────────────────────────────────── */
  'chrome.search': 'Search or jump to…',
  'chrome.period': 'Reporting period',
  'chrome.account': 'Account',
  'chrome.accountPassword': 'Account & password',
  'chrome.signOut': 'Sign out',
  'chrome.owner': 'Owner',
  'chrome.admin': 'Admin',
  'chrome.language': 'Language',
  'chrome.workspace': 'Workspace',
  'chrome.expandSidebar': 'Expand sidebar',
  'chrome.collapseSidebar': 'Collapse sidebar',
  'chrome.breadcrumb': 'Breadcrumb',
  'chrome.goTo': 'Go to',
  'chrome.collectData': 'Collect data',

  /* ── shared ────────────────────────────────────────────────────────── */
  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.tco2e': 'tCO₂e',
  'common.months': 'months',
  // Month ledger + upload modal (fixed hardcoded Albanian, 2026-10-22)
  'ledger.title': 'Recorded',
  'ledger.emptyHint': 'No activity yet — record the first one.',
  'ledger.confirmDelete': 'Delete this record? Its emissions are removed from the report.',
  'ledger.deleteFailed': 'Delete failed. Try again.',
  'ledger.prevMonth': 'Previous month',
  'ledger.nextMonth': 'Next month',
  'upload.badFormat': '{name}: format not supported',
  'upload.tooBig': '{name}: file is larger than 10MB',
  'upload.dropText': 'Drop the invoice here or browse',
  'upload.dropHint': 'PNG, JPG, PDF up to 10MB',
  'upload.single': 'Upload invoice',
  'upload.bulk': 'Upload invoices',
  'common.all': 'All',
  'common.none': '—',

  /* ── overview ──────────────────────────────────────────────────────── */
  'overview.title': 'Overview',
  'overview.desc': 'Corporate carbon footprint{year}, consolidated across {count} {facilities}. GHG Protocol operational-control boundary.',
  'overview.facility': 'facility',
  'overview.facilities': 'facilities',
  'overview.loading': 'Loading your corporate carbon footprint…',
  'overview.loadError': 'Could not load your dashboard.',
  'overview.reports': 'Reports',
  'overview.addData': 'Add data',
  'overview.noData': 'No emissions recorded on this account yet.',
  'overview.noDataBody': 'Start with the guided monthly registration, or add a single activity from the Data collection menu.',
  'overview.kpi.total': 'Total — {period}',
  'overview.kpi.currentPeriod': 'current period',
  'overview.kpi.intensity': 'Emission intensity',
  'overview.kpi.floorArea': '{area} m² floor area',
  'overview.kpi.noFloorArea': 'No floor area recorded',
  'overview.kpi.completeness': 'Inventory completeness',
  'overview.kpi.completenessFoot': '{complete} complete · {partial} partial',
  'overview.kpi.vsBaseline': 'vs {year}',
  'overview.kpi.baseline': 'baseline',
  'overview.kpi.noBaseline': 'No baseline-year data to compare against',
  'overview.kpi.onTrack': 'On track for {year}',
  'overview.kpi.offTrack': 'Off track for {year}',
  'overview.scopeSplit': 'Footprint by scope',
  'overview.scopeSplitSub': 'GHG Protocol categories · last 12 months',
  'overview.scope1': 'Scope 1 — direct',
  'overview.scope2': 'Scope 2 — purchased energy',
  'overview.scope3': 'Scope 3 — value chain',
  'overview.monthly': 'Monthly emissions',
  'overview.coverage': 'Data coverage',

  /* ── action inbox ──────────────────────────────────────────────────── */
  'inbox.title': 'Action inbox',
  'inbox.desc': 'Everything standing between you and an audit-ready inventory, most urgent first.',
  'inbox.cardTitle': 'Action inbox',
  'inbox.cardSub': 'What stands between you and an audit-ready inventory',
  'inbox.open': '{count} open',
  'inbox.clear': 'Clear',
  'inbox.seeAll': 'See all {count} items',
  'inbox.openInbox': 'Open action inbox',
  'inbox.emptyTitle': 'Nothing is blocking your inventory',
  'inbox.emptyBody': 'Every facility has data for each month elapsed so far this year.',
  'inbox.emptyBodyYear': 'Every facility has data for each of the {count} months elapsed in {year}.',
  'inbox.loading': 'Working out what stands between you and an audit-ready inventory…',
  'inbox.stat.open': 'Open items',
  'inbox.stat.openFootNone': 'Nothing outstanding',
  'inbox.stat.openFoot': 'Each row below is one fix',
  'inbox.stat.covered': 'Site-months covered · {year}',
  'inbox.stat.coveredFoot': '{pct}% of months elapsed so far',
  'inbox.stat.noFacilities': 'No facilities yet',
  'inbox.stat.partial': 'Months missing a scope',
  'inbox.stat.partialFoot': 'Recorded, but only Scope 1 or Scope 2',
  'inbox.elapsedNote': 'Coverage is measured against months that have already elapsed in {year} — future months are not counted as gaps.',
  'inbox.group.missing': 'Missing data',
  'inbox.group.missingSub': 'No record of any kind for these site-months',
  'inbox.group.partial': 'Incomplete months',
  'inbox.group.partialSub': 'A record exists but one scope is absent, so the month understates the footprint',
  'inbox.group.attribution': 'Attribution',
  'inbox.group.trajectory': 'Trajectory',
  'inbox.group.info': 'For information',
  'inbox.task.noData': '{site} — {month} {year} has no data',
  'inbox.task.noDataBody': 'Gaps weaken the inventory and block assurance. Start with Scope 1 stationary combustion.',
  'inbox.task.addData': 'Add data',
  'inbox.task.missingScope': '{site} — {month} {year} is missing {scope}',
  'inbox.task.oneScope': 'one scope',
  'inbox.task.missingS2Body': 'Fuel is recorded but purchased energy is not, so this month understates the footprint.',
  'inbox.task.missingS1Body': 'Purchased energy is recorded but direct combustion is not.',
  'inbox.task.oneScopeBody': 'Only one of Scope 1 / Scope 2 is recorded for this month.',
  'inbox.task.complete': 'Complete',
  'inbox.task.unassigned': '{count} records are not linked to a facility',
  'inbox.task.unassignedBody': 'These are excluded from per-site intensity and from the coverage grid.',
  'inbox.task.reviewRecords': 'Review records',
  'inbox.task.offTrack': 'Off track against the {year} target',
  'inbox.task.offTrackBody': '{pct}% vs {baseline}, where {required}% is required at this point.',
  'inbox.task.openExplorer': 'Open explorer',
  'inbox.task.biogenic': '{value} tCO₂ biogenic reported separately',
  'inbox.task.biogenicBody': 'Wood and pellet biomass — correctly excluded from the Scope 1 total. Nothing to do.',

  /* ── entry stepper ─────────────────────────────────────────────────── */
  'entry.step1': 'Choose site',
  'entry.step2': 'Add data',
  'entry.step3': 'Review & post',
  'entry.scopeNote.1': 'Direct emissions from sources you own or control.',
  'entry.scopeNote.2': 'Indirect emissions from the energy you purchase and consume.',
  'entry.scopeNote.3': 'Indirect emissions across your value chain, outside your direct control.',
  'entry.card1': '1 · Where does this data belong?',
  'entry.card2': '2 · How do you want to add it?',
  'entry.card2SubOcr': 'Scanning is fastest and keeps the source document attached for audit.',
  'entry.card2SubNoOcr': 'Attach the source document — this is the evidence an audit asks for.',
  'entry.sourceDoc': 'Scanned document',
  'docprev.loading': 'Loading document…',
  'docprev.alt': 'Source document preview',
  'entry.card3': '3 · Review and post',
  'entry.card3Sub': 'Confirm the figures before they enter the inventory.',
  'entry.card3Saved': '3 · Activity recorded',
  'entry.card3SavedSub': "The record is part of this month's inventory.",
  'entry.saved': 'Saved',
  'entry.change': 'Change',
  'entry.continue': 'Continue',
  'entry.back': 'Back',
  'entry.backFix': 'Back & correct',
  'entry.post': 'Post to inventory',
  'entry.posting': 'Saving…',
  'entry.addAnother': 'Add another activity',
  'entry.finish': 'Finish',
  'entry.building': 'Site',
  'entry.selectBuilding': '-- Select a site --',
  'entry.buildingHint': 'Records must attach to a site so intensity metrics stay correct.',
  'entry.purpose': 'Activity purpose',
  'entry.purposeUnset': '-- Not specified --',
  'entry.purposeHint': 'Used to split Scope {scope} across end-uses in reports.',
  'entry.period': 'Period',
  'entry.mode.scan': 'Scan document',
  'entry.mode.manual': 'Enter manually',
  'entry.mode.csv': 'Import CSV',
  'entry.method': 'Method',
  'entry.method.scan': 'Document attached',
  'entry.method.manual': 'Manual entry',
  'entry.method.csv': 'CSV import',
  'entry.document': 'Document',
  'entry.notAttached': 'Not attached',
  'entry.drop': 'Drop {doc} here',
  'entry.dropHint': 'PDF, PNG or JPG up to 10 MB · or',
  'entry.browse': 'Browse files',
  'entry.browseOther': 'Choose a different file',
  'entry.ocrOn': 'We read the supplier, meter registers and billing period automatically, then ask you to confirm anything below 90% confidence.',
  'entry.ocrOffTitle': 'Automatic reading is not enabled for this document type yet.',
  'entry.ocrOffBody': 'The document is attached as evidence and stays linked to the record — enter the figures below.',
  'entry.csvOffTitle': 'CSV import is not enabled yet.',
  'entry.csvOffBody': 'Until then use "Enter manually", or attach the source document.',
  'entry.csvDrop': 'a CSV file',
  'entry.csvHint': 'One row per reading · CSV or XLSX',
  'entry.calc': 'Calculate',
  'entry.calculating': 'Calculating…',
  'entry.emissions': 'Calculated emissions',
  'entry.coverage': 'Coverage for {period}',
  'entry.scopeComplete': 'This scope is complete for the month.',
  'entry.continueTo': 'Continue to {label}',
  'entry.viewRecords': 'View records',
  'entry.uploadOk': 'Document attached and stored as evidence.',
  'entry.uploadTooBig': 'The document is over 10 MB.',
  'entry.uploadNoBuilding': 'Choose a site before attaching the document.',
  'entry.uploadFailed': 'Upload failed: {message}',

  /* ── category context (right column) ───────────────────────────────── */
  'ctx.latest': 'Latest for this category',
  'ctx.latestSub': 'Last 12 months · all sites',
  'ctx.noHistory': 'No history yet',
  'ctx.noHistoryBody': 'Nothing recorded for {category} in the last 12 months. The first record you post starts the trend.',
  'ctx.loadFail': 'Could not read the data',
  'ctx.loadFailBody': 'Refresh the page to try again.',
  'ctx.missing': 'Missing periods',
  'ctx.missingSub': "Gaps that will weaken this year's inventory",
  'ctx.noGaps': 'No gaps — every site is covered.',
  'ctx.noFacilities': 'No sites',
  'ctx.noFacilitiesBody': 'Add a site before recording data.',
  'ctx.monthsNoData': '{count} months with no data',
  'ctx.add': 'Add',

  /* ── entry screens ─────────────────────────────────────────────────── */
  'stationary.title': 'Stationary combustion',
  'stationary.desc': 'Fuel burned in boilers, furnaces and generators you own or control.',
  'stationary.fuel': 'Fuel',
  'stationary.fuelPlaceholder': 'Fuel type',
  'stationary.periodType': 'Period',
  'stationary.startMonth': 'Start month',
  'stationary.quantity': 'Quantity',
  'stationary.doc': 'the fuel invoice',
  'stationary.biogenicTitle': 'Biogenic CO₂.',
  'stationary.biogenicBody': 'Reported for transparency, not included in the Scope 1 total.',
  'stationary.biogenicNote': 'Biogenic CO₂ (wood/pellet) — reported separately, not in the Scope 1 total.',
  'stationary.ledgerEmpty': 'No fuel recorded for this month — calculate and save the first one.',
  'stationary.success': '{value} tCO₂e added to Scope 1 · Stationary combustion · {period}.',

  'mobile.title': 'Mobile combustion',
  'mobile.desc': 'Fuel burned in vehicles you own or lease.',
  'mobile.vehicleType': 'Vehicle type',
  'mobile.vehicleId': 'Vehicle ID',
  'mobile.vehicleIdPlaceholder': 'e.g. AA-123-BB or the vehicle name',
  'mobile.registered': 'Registered: {list}',
  'mobile.category': 'Vehicle category',
  'mobile.vehicle': 'Vehicle',
  'mobile.method': 'Recording method',
  'mobile.methodHint': 'Use distance when you have no fuel invoice.',
  'mobile.byFuel': 'By fuel',
  'mobile.byDistance': 'By distance',
  'mobile.fuelType': 'Fuel type',
  'mobile.selectFuel': 'Select a fuel',
  'mobile.distanceType': 'Vehicle type (for distance)',
  'mobile.distanceMethod': 'Distance travelled',
  'mobile.month': 'Month',
  'mobile.evNote': 'Electric vehicles have no direct emissions (Scope 1). Their energy use is reported under Scope 2.',
  'mobile.evAction': 'Record consumption (Scope 2)',
  'mobile.hybridNote': 'For hybrids, record the fuel here (Scope 1); charging energy is recorded separately under Scope 2.',
  'mobile.hybridPrompt': 'Would you like to record the charging energy for this hybrid vehicle?',
  'mobile.hybridAction': 'Record charging (Scope 2)',
  'mobile.later': 'Later',
  'mobile.combined': 'Combined total — hybrid',
  'mobile.combinedFuel': 'Fuel share (Scope 1)',
  'mobile.combinedCharge': 'Charging share (Scope 2)',
  'mobile.combinedTotal': 'Combined total',
  'mobile.noCharging': 'No charging record found. Add a Scope 2 record (hybrid_charging) for the same site and period to include it in the total.',
  'mobile.ledgerEmpty': 'No vehicle recorded for this month — calculate and save the first one.',
  'mobile.success': '{value} tCO₂e added to Scope 1 · Mobile combustion · {period}.',

  /* ── records ───────────────────────────────────────────────────────── */
  'rec.title': 'Records',
  'rec.desc': 'Every record in the inventory, with the calculation trace behind each figure.',
  'rec.loading': 'Loading…',
  'rec.search': 'Search document, category or facility…',
  'rec.facility': 'Facility',
  'rec.period': 'Period',
  'rec.sourceDoc': 'Source document',
  'rec.record': 'Record',
  'rec.result': 'Result',
  'rec.trace': 'Calculation trace',
  'rec.noMatch': 'No records match these filters',
  'rec.noMatchBody': 'Try clearing the search or widening the facility and source filters.',

  /* ── data quality ──────────────────────────────────────────────────── */
  'dq.title': 'Data quality',
  'dq.desc': 'Four separate measures rather than one score — each finding names its own cause.',
  'dq.loading': 'Loading…',
  'dq.verified': 'Verified',
  'dq.evidenced': 'Evidenced',
  'dq.completeness': 'Completeness',
  'dq.findings': 'Open findings',
  'dq.findingsTitle': 'Findings',
  'dq.findingsSub': 'Ordered by impact on the reported total',
  'dq.nothing': 'Nothing to resolve',
  'dq.nothingBody': 'Every record reconciles and every site-month has data.',
  'dq.evidenceBasis': 'Evidence basis',
  'dq.evidenceBasisSub': 'Share of the footprint by what stands behind it',
  'dq.coverageFacility': 'Coverage by facility',
  'dq.coverageMatrix': 'Coverage matrix',
  'dq.noFacilities': 'No facilities',
  'dq.noFacilitiesBody': 'Add a facility to track coverage.',

  /* ── explorer ──────────────────────────────────────────────────────── */
  'exp.title': 'Emissions explorer',
  'exp.desc': 'Drill the footprint from scope down to the individual fuel or activity.',
  'exp.total': 'Total',
  'exp.totalSub': 'Across all scopes in this selection',
  'exp.byScope': 'By scope',
  'exp.byScopeSub': 'Share of the selected footprint',
  'exp.breakdown': 'Breakdown',
  'exp.breakdownSub': 'Expand a group to see the individual fuels and activities behind it',
  'exp.largest': 'Largest sources',
  'exp.trend': 'Monthly trend',
  'exp.trendSub': 'Last 6 months · the black tick marks the −10% year-on-year goal for that month',
  'exp.noEmissions': 'No emissions in this selection',
  'exp.noEmissionsBody': 'Try a wider time range, or a different facility.',
  'exp.noMonthly': 'No monthly data',
  'exp.nothingToRank': 'Nothing to rank',
  'exp.s1stationary': 'Scope 1 — stationary combustion',
  'exp.s1mobile': 'Scope 1 — mobile combustion',
  'exp.s1process': 'Scope 1 — process emissions',
  'exp.s2': 'Scope 2 — purchased energy',
  'exp.biogenic': 'Biogenic CO₂ — memo, excluded from totals',

  /* ── facilities ────────────────────────────────────────────────────── */
  'fac.title': 'Facilities',
  'fac.desc': 'Facilities define the reporting boundary.',
  'fac.sub': 'Facilities define the reporting boundary',
  'fac.empty': 'No facilities yet',
  'fac.emptyBody': 'Add the sites in your reporting boundary — every emissions record attaches to one.',
  'fac.profile': 'Site profile',
  'fac.inBoundary': 'Sites in boundary',
  'fac.totalArea': 'Total floor area',
  'fac.avgIntensity': 'Avg intensity',
  'fac.intensity': 'Intensity',
  'fac.coverage': 'Data coverage',
  'fac.total': 'Total',

  /* ── account ───────────────────────────────────────────────────────── */
  'acc.title': 'Account',
  'acc.desc': 'Your profile, plan and password.',
  'acc.details': 'Your details',
  'acc.detailsSub': 'Used on the GHG report cover and in exports.',
  'acc.identity': 'Identity',
  'acc.identitySub': 'Set when your account was created — contact support to change your email.',
  'acc.companySize': 'Company size',
  'acc.password': 'Password',
  'acc.currentPassword': 'Current password',
  'acc.newPassword': 'New password',
  'acc.confirmPassword': 'Confirm new password',

  /* ── scope 2 electricity ───────────────────────────────────────────── */
  'elec.title': 'Electricity bills',
  'elec.desc': 'Grid electricity bought and consumed — location-based method.',
  'elec.doc': 'the electricity bill',
  'elec.purpose': 'Consumption purpose',
  'elec.purposeBuilding': 'Whole-building consumption',
  'elec.purposeEv': 'Electric vehicle charging',
  'elec.purposeHybrid': 'Hybrid vehicle charging',
  'elec.usage': 'Usage',
  'elec.vehicle': 'Vehicle',
  'elec.selectVehicle': '-- Select a vehicle --',
  'elec.consumption': 'Consumption',
  'elec.billingPeriod': 'Billing period',
  'elec.totalConsumption': 'Total consumption',
  'elec.register': 'Register {name}',
  'elec.sourceDoc': 'Source document',
  'elec.ledgerEmpty': 'No bill recorded for this month — scan or enter the first one.',
  'elec.success': '{value} tCO₂e added to Scope 2 · Electricity · {period}.',
  'elec.errBuilding': 'Please choose a site before saving',
  'elec.errPeriod': 'Please set the billing period',
  'elec.errCalc': 'Please calculate the bill before saving',
  'elec.processing': 'Processing bills…',
  'elec.progress': '{current} of {total}',
  'elec.reading': 'Reading the document…',
  'elec.bulkUpload': 'Upload several bills',
  'elec.bulkOnlyBuilding': 'Bulk upload applies to whole-building electricity only.',
  'elec.pickBuildingFirst': 'Please choose a site before uploading the bill',
  'elec.ocrFailTitle': 'The bill could not be read',
  'elec.ocrFailBody': 'Please upload a clearer image, or enter the figures manually.',
  'elec.ocrFailManual': 'Enter manually',
  'elec.ocrFailRetry': 'Upload another bill',

  /* ── scope 1 process ───────────────────────────────────────────────── */
  'proc.title': 'Process emissions',
  'proc.desc': 'Emissions released by a physical or chemical process, not by combustion.',
  'proc.applicableQ': 'Does your company run industrial processes that directly generate CO₂ emissions (other than burning fuel)?',
  'proc.applicableHint': 'For example cement/clinker, lime, glass, ceramics, steel or aluminium production, or the use of soda ash / limestone / dolomite.',
  'proc.yes': 'Yes, we run industrial processes',
  'proc.no': 'None of the above',
  'proc.notIdentified': 'No process emissions identified',
  'proc.notIdentifiedBody': 'This section stays empty for this company. You can change the answer at any time.',
  'proc.changeAnswer': 'Change answer',
  'proc.type': 'Process type',
  'proc.selectType': '-- Select a process --',
  'proc.doc': 'the production log',
  'proc.ledgerEmpty': 'No process recorded for this month — calculate and save the first one.',
  'proc.success': '{value} tCO₂e added to Scope 1 · Process emissions · {period}.',

  /* ── scope 2 district heat & cooling ───────────────────────────────── */
  'heat.title': 'Heating & cooling',
  'heat.desc': 'Heat or chilled water purchased from an external network.',
  'heat.type': 'Type',
  'heat.selectType': '-- Select a type --',
  'heat.fuel': 'Energy source',
  'heat.selectFuel': '-- Select a source --',
  'heat.doc': 'the heating invoice',
  'heat.ledgerEmpty': 'Nothing recorded for this month — calculate and save the first one.',
  'heat.success': '{value} tCO₂e added to Scope 2 · Heating & cooling · {period}.',

  /* ── scope 3 ───────────────────────────────────────────────────────── */
  's3.title': 'Travel & commuting',
  's3.desc': 'Value-chain emissions — business travel, and employees journeying between home and work.',
  's3.category': 'Category',
  's3.selectCategory': '-- Select a category --',
  's3.activity': 'Activity',
  's3.selectActivity': '-- Select an activity --',
  's3.notReady': 'This category is not enabled yet — there are no emission factors for it.',
  's3.employees': 'Total employees',
  's3.workDays': 'Working days / month',
  's3.survey': 'Commuting survey',
  's3.surveySub': 'Split employees by travel mode — the percentages should add up to 100%.',
  's3.mode': 'Mode',
  's3.share': 'Share',
  's3.avgDistance': 'Average distance (km, round trip)',
  's3.allocated': 'Allocated: {pct}%',
  's3.overAllocated': 'The percentages exceed 100% — correct them before saving.',
  's3.underAllocated': 'The percentages do not reach 100%. Saving is allowed, but this covers only {pct}% of employees.',
  's3.commuting': 'Employee commuting',
  's3.doc': 'the supporting document',
  's3.ledgerEmpty': 'No Scope 3 record for this month — calculate and save the first one.',
  's3.success': '{value} tCO₂e added to Scope 3 · {period}.',

  /* ── reports ───────────────────────────────────────────────────────── */
  'reports.title': 'Reports',
  'reports.desc': 'The GHG inventory behind your PDF report — same assembler, same figures, so what you export is what you see here.',
  'reports.loading': 'Assembling your GHG inventory…',
  'reports.loadFail': 'Could not load the report',
  'reports.loadFailBody': 'The dashboard endpoint did not respond. Your data is not affected.',
  'reports.uploadLogo': 'Upload logo',
  'reports.changeLogo': 'Change logo',
  'reports.removeLogo': 'Remove logo',
  'reports.logoTitle': 'Company logo shown on the PDF report cover',
  'reports.exportCsv': 'Export CSV',
  'reports.exportPdf': 'Export PDF',
  'reports.exporting': 'Exporting…',
  'reports.timeRange': 'Time range',
  'reports.range.lastMonth': 'Last month',
  'reports.range.last3': 'Last 3 months',
  'reports.range.last6': 'Last 6 months',
  'reports.range.lastYear': 'Last year',
  'reports.range.allTime': 'All time',
  'reports.scope': 'Scope',
  'reports.facility': 'Facility',
  'reports.filtered': 'Filtered to {scope}',
  'reports.breakdown': 'Emissions breakdown',
  'reports.breakdownSub': 'Monthly total against the −10% year-on-year goal (black tick)',
  'reports.nothingToPlot': 'Nothing to plot',
  'reports.nothingToPlotBody': 'No records fall inside this filter.',
  'reports.byScope': 'Emissions by scope',
  'reports.byScopeSub': 'GHG Protocol categories',
  'reports.noEmissions': 'No emissions recorded',
  'reports.noEmissionsBody': 'Add data to see the scope split.',
  'reports.most': 'Most emissions',
  'reports.mostSub': 'Largest sources in the selected period',
  'reports.noSources': 'No emission sources',
  'reports.noSourcesBody': 'Nothing recorded for this scope in this period.',
  'reports.scope1': 'Scope 1 — direct emissions',
  'reports.scope2': 'Scope 2 — purchased energy',
  'reports.scope3': 'Scope 3 — value chain',
  'reports.scope3Sub': 'Business travel and commuting · 2 of 15 categories',
  'reports.stackedBy': 'Stacked by activity · totals in tCO₂e',
  'reports.vsLastYear': '{sign}{value} tCO₂e vs last year',
  'reports.last8': 'Last 8 months',
  'reports.last12': 'Last 12 months',
  'reports.allTime': 'All time',
  'reports.biogenic': 'Biogenic CO₂',
  'reports.biogenicSub': 'Reported in parallel — excluded from the Scope 1 total, per GHG Protocol & ISO 14064',
  'reports.parallel': 'Parallel reporting',
  'reports.fuel': 'Fuel',
  'reports.records': 'Records',
  'reports.quantity': 'Quantity',
  'reports.kpi.inventory': 'GHG inventory {year} (report total)',
  'reports.kpi.evidence': 'Evidence on file — {year}',
  'reports.kpi.evidenceUnit': 'records',
  'reports.kpi.evidenceFoot': '{count} {facilities} · shown on the PDF report',
  'reports.kpi.firstYear': 'First inventory year — no {year} data',
  'reports.kpi.vsBaseline': 'vs {year} baseline',
  'reports.kpi.scopeSplit': 'Scope split',
  'reports.kpi.scopeSplitFoot': 'Scope 1 / 2 / 3 share of the total',
  'reports.kpi.biogenic': 'Biogenic CO₂',
  'reports.kpi.biogenicFoot': 'Reported in parallel, not in Scope 1',
  'reports.bannerMissing': '{period} is missing data: {missing}.',
  'reports.bannerAdd': 'Add the data',
  'reports.bannerDismiss': 'Dismiss',
  'reports.biogenicSeparate': 'Biogenic {value} tCO₂e is reported separately — see the Biogenic CO₂ section.',

  /* ── CBAM ──────────────────────────────────────────────────────────── */
  'cbam.overview.title': 'CBAM overview',
  'cbam.overview.desc': 'Embedded emissions per exported good, completeness checks and the indicative certificate cost curve 2026–2034.',
  'cbam.installations.title': 'Installations',
  'cbam.installations.desc': 'Production installations outside the EU — location, operator and the carbon price for each reporting year.',
  'cbam.goods.title': 'Goods & precursors',
  'cbam.goods.desc': 'The CBAM goods you export (CN code, production tonnage) and their precursors, on supplier or default values.',
  'cbam.gate.title': 'The CBAM module needs the Compliance & Export plan',
  'cbam.gate.body': 'Work out the embedded emissions (SEE) of the goods you export to the EU, prepare the operator\'s emissions report and estimate CBAM certificate costs through 2034.',
  'cbam.gate.cta': 'Upgrade to Compliance & Export',
  'cbam.gate.info': 'From 1 January 2026 CBAM is in its definitive period: as the operator of an exporting installation you are required to monitor and calculate embedded emissions and have them verified (Regulation (EU) 2023/956; IR (EU) 2025/2547). The first declaration for 2026 imports is due by 30 September 2027; certificate sales start on 1 February 2027.',
  'cbam.form.cancel': 'Cancel',

  /* — overview (analysis surface — English in both dictionaries) — */
  'cbam.ov.exportSee': 'EC SEE file',
  'cbam.ov.exportedSee': 'EC SEE Communication file downloaded — open it in Excel (the Summary sheets compute on open).',
  'cbam.ov.exportSeeHint': "The European Commission's own CBAM Communication template, prefilled with this installation's data",
  'cbam.ov.export': 'Export workbook (.xlsx)',
  'cbam.ov.exporting': 'Exporting…',
  'cbam.ov.exportHint': 'Register an installation first — the workbook is generated per installation.',
  'cbam.ov.exported': 'Workbook downloaded',
  'cbam.ov.exportFailed': 'Download failed. Try again.',
  'cbam.ov.kpiGoods': 'Goods covered',
  'cbam.ov.kpiProduction': 'Total production',
  'cbam.ov.kpiSee': 'Weighted SEE (direct)',
  'cbam.ov.kpiCost': 'Indicative cost {year}',
  'cbam.ov.kpiCostFoot': 'at {price} €/tCO₂e — EU ETS assumption',
  'cbam.ov.goodsTitle': 'Embedded emissions per good',
  'cbam.ov.goodsSub': 'Click a row for the full calculation trace.',
  'cbam.ov.indirectMemo': 'reported, not charged',
  'cbam.ov.deMinimis': 'Under 50 t exported to the EU. An importer whose cumulative CBAM-goods imports stay at or below 50 t in the calendar year is exempt (Art 2a) — the exemption belongs to the importer, never to the operator, and splitting consignments counts as circumvention.',
  'cbam.ov.empty': 'No installations yet',
  'cbam.ov.emptyBody': 'Register your production installation, add its processes and goods — the overview computes embedded emissions and certificate costs from there.',
  'cbam.ov.emptyCta': 'Go to installations',
  'cbam.ov.noGoods': 'No goods for {year}',
  'cbam.ov.noGoodsBody': 'Add the goods you exported in this reporting year — each CN code brings its sector, default value and benchmark.',
  'cbam.ov.noGoodsCta': 'Go to goods & precursors',
  'cbam.ov.costTitle': 'Indicative certificate cost 2026–2034',
  'cbam.ov.costSub': 'max(0, chargeable SEE × EU export − free allocation) × ETS price, phased in by year.',
  'cbam.ov.etsAssumption': 'indicative — EU ETS price assumption',
  'cbam.ov.etsExportsNote': 'The workbook and trace-PDF exports use this price for their indicative cost columns.',
  'cbam.ov.etsInvalid': 'The assumption must be above 0 €/t — the server default applies until then.',
  'cbam.ov.costEmpty': 'The cost curve appears once a good has production and EU-export tonnage.',
  'cbam.ov.timeline': 'Certificate sales start 1 February 2027; the first declaration, covering 2026 imports, is due by 30 September 2027. No certificates are bought during 2026.',
  'cbam.ov.completeTitle': 'Completeness',
  'cbam.ov.completeSub': 'What the operator\'s emissions report still needs.',
  'cbam.ov.completeAllOk': 'Everything the report needs is in place.',
  'cbam.ov.fix': 'Fix',
  'cbam.ov.defaultsTitle': 'Defaults cost you money.',
  'cbam.ov.defaultsBody': 'Default values carry a mark-up (+10% in 2026, +20% in 2027, +30% from 2028) on top of conservative country averages — actual data usually beats them. Ask your suppliers for verified embedded-emissions values, or book verification for your own process.',
  'cbam.ov.defaultsDelta': '{cn}: {delta} tCO₂e/t of its SEE rides on default values — up to {eur} € of the {year} cost.',
  'cbam.ov.defaultsDeltaNoEur': '{cn}: {delta} tCO₂e/t of its SEE rides on default values.',
  'cbam.ov.seeTitle': 'Specific embedded emissions',
  'cbam.ov.traceTitle': 'Calculation trace',
  'cbam.ov.traceEmpty': 'The server returned no trace for this good.',
  'cbam.ov.precTitle': 'Precursors',
  'cbam.ov.precEmpty': 'No precursors recorded — only the installation\'s own process emissions are embedded.',
  'cbam.ov.boundary': 'System boundary for aluminium products: fuel combustion and flue-gas treatment only — cutting, welding and finishing stay outside. If those are your only emission sources, a near-zero own-process SEE is legitimate.',
  'cbam.ov.tracePdf': 'Download trace PDF',
  'cbam.ov.close': 'Close',

  /* — installations: list + wizard — */
  'cbam.inst.new': 'New installation',
  'cbam.inst.backToList': 'Back to the list',
  'cbam.inst.empty': 'No installations yet',
  'cbam.inst.emptyBody': 'Register your production installation — location, operator and the reporting year — then add its processes and goods.',
  'cbam.inst.step1': 'Pick the location',
  'cbam.inst.step2': 'Installation details',
  'cbam.inst.step3': 'Reporting year',
  'cbam.inst.card1': 'Where is the installation?',
  'cbam.inst.card1Sub': 'Link it to an existing building — the emissions recorded there feed the SEE calculation.',
  'cbam.inst.building': 'Building',
  'cbam.inst.selectBuilding': 'Select a building',
  'cbam.inst.standalone': 'Standalone — enter the address manually',
  'cbam.inst.standaloneShort': 'Standalone installation',
  'cbam.inst.buildingHint': 'Picking a building prefills the country.',
  'cbam.inst.country': 'Country',
  'cbam.inst.countryPh': 'e.g. Bosnia and Herzegovina',
  'cbam.inst.city': 'City',
  'cbam.inst.address': 'Address',
  'cbam.inst.card2': 'Installation details',
  'cbam.inst.card2Sub': 'How the installation appears in the CBAM registry and to your importers.',
  'cbam.inst.name': 'Installation name',
  'cbam.inst.unlocode': 'UN/LOCODE',
  'cbam.inst.unlocodeHint': 'The UN location code (e.g. BA SJJ) — optional, but the CBAM registry asks for it to identify the site.',
  'cbam.inst.operatorName': 'Operator name',
  'cbam.inst.operatorEmail': 'Operator email',
  'cbam.inst.registryId': 'CBAM registry ID',
  'cbam.inst.registryIdHint': 'The installation ID once you register in the CBAM registry — it can be filled in later.',
  'cbam.inst.o3ci': 'Registered in the O3CI module',
  'cbam.inst.o3ciCallout': 'The O3CI module of the CBAM registry lets a non-EU operator upload the monitoring plan and the emissions report ONCE and share them with every EU importer — instead of emailing each one separately. Registration is voluntary but strongly recommended; keep your registry ID here once you have it.',
  'cbam.inst.card3': 'Reporting year',
  'cbam.inst.card3Sub': 'The carbon price paid in the country of origin and the verification status.',
  'cbam.inst.year': 'Year',
  'cbam.inst.carbonPrice': 'Carbon price paid',
  'cbam.inst.noCarbonPrice': '{country} has no carbon price today — leave it at 0.',
  'cbam.inst.carbonPriceHint': 'The price effectively paid per tCO₂e in the country of origin, net of rebates — it is deducted from the CBAM liability.',
  'cbam.inst.instrument': 'Instrument',
  'cbam.inst.verification': 'Verification status',
  'cbam.inst.ver.unverified': 'Unverified',
  'cbam.inst.ver.verifier_engaged': 'Verifier engaged',
  'cbam.inst.ver.verified': 'Verified',
  'cbam.inst.verifierName': 'Verifier name',
  'cbam.inst.coverageNote': 'Coverage note',
  'cbam.inst.create': 'Create the installation',
  'cbam.inst.creating': 'Creating…',
  'cbam.inst.created': 'Installation created',
  'cbam.inst.updated': 'Installation updated',
  'cbam.inst.deleted': 'Installation deleted',
  'cbam.inst.saveFailed': 'Could not save. Try again.',
  'cbam.inst.saveCta': 'Save changes',
  'cbam.inst.yearSaveCta': 'Save',
  'cbam.inst.vName': 'The installation name is required.',
  'cbam.inst.confirmDelete': 'Delete {name}? Its processes and reporting years go with it; goods pointing at those processes lose their process.',
  'cbam.inst.edit': 'Edit installation',
  'cbam.inst.addYear': 'Add reporting year',
  'cbam.inst.editYear': 'Reporting year {year}',
  'cbam.inst.yearSaved': 'Reporting year saved',
  'cbam.inst.contextTitle': 'How this works',
  'cbam.inst.contextBody': 'The installation is the unit the CBAM registry recognises: the physical site where the goods are made. Each installation has production processes, and every exported good belongs to one process. The emissions recorded in Karbon360 for the linked building are split between processes by the attribution shares.',

  /* — processes + attribution — */
  'cbam.proc.add': 'Add process',
  'cbam.proc.new': 'New production process',
  'cbam.proc.edit': 'Edit process',
  'cbam.proc.name': 'Process name',
  'cbam.proc.namePh': 'e.g. Door & window production',
  'cbam.proc.sector': 'Sector',
  'cbam.proc.category': 'Aggregated goods category',
  'cbam.proc.route': 'Production route',
  'cbam.proc.routeHint': 'The route must match how the good is really made — default values and benchmarks differ by route.',
  'cbam.route.K': 'K — Primary: from ore (e.g. electrolytic aluminium)',
  'cbam.route.L': 'L — Secondary: remelted scrap',
  'cbam.route.C': 'C — Carbon steel via blast furnace (BF–BOF)',
  'cbam.route.D': 'D — Direct reduced iron + electric arc furnace (DRI–EAF)',
  'cbam.route.E': 'E — Electric arc furnace, scrap-based (EAF)',
  'cbam.proc.attr': 'Attribution',
  'cbam.proc.attrSub': 'How much of the installation\'s inventory belongs to this process — leave 100/100 when it is the only process.',
  'cbam.proc.attrYear': 'Year',
  'cbam.proc.directShare': 'Share of direct emissions',
  'cbam.proc.elecShare': 'Share of electricity',
  'cbam.proc.inclStationary': 'Include stationary combustion',
  'cbam.proc.inclMobile': 'Include mobile combustion',
  'cbam.proc.inclMobileHint': 'Road vehicles outside the installation boundary stay out — leave this off if unsure.',
  'cbam.proc.inclProcess': 'Include process emissions',
  'cbam.proc.methodNote': 'How the split was derived',
  'cbam.proc.methodNotePh': 'e.g. sub-metering, floor area, machine hours…',
  'cbam.proc.boundaryAl': 'System boundary for aluminium products: fuel combustion and flue-gas treatment only. Cutting, welding and finishing are OUTSIDE the boundary — do not attribute their emissions to this process.',
  'cbam.proc.saved': 'Process saved',
  'cbam.proc.deleted': 'Process deleted',
  'cbam.proc.saveCta': 'Save the process',
  'cbam.proc.vRequired': 'Name, sector and goods category are required.',
  'cbam.proc.confirmDelete': 'Delete process "{name}"? Goods assigned to it lose their process.',

  /* — goods & precursors — */
  'cbam.goods.new': 'Add good',
  'cbam.goods.empty': 'No goods yet',
  'cbam.goods.emptyBody': 'Add the goods you export to the EU — the CN code reveals the sector, the default value and the benchmark automatically.',
  'cbam.goods.needProcess': 'First create an installation with at least one production process — every good belongs to a process.',
  'cbam.goods.toInstallations': 'Go to installations',
  'cbam.goods.modalNew': 'Add a CBAM good',
  'cbam.goods.modalEdit': 'Edit good',
  'cbam.goods.process': 'Production process',
  'cbam.goods.year': 'Year',
  'cbam.goods.cn': 'CN code',
  'cbam.goods.cnHint': 'Type at least 6 digits — the code is checked live against Annex I.',
  'cbam.goods.description': 'Description',
  'cbam.goods.descriptionPh': 'e.g. Two-sash aluminium window',
  'cbam.goods.route': 'Production route',
  'cbam.goods.routeMismatch': 'The default value for this CN/country assumes route {route} — if your route differs, the default does not match.',
  'cbam.goods.production': 'Annual production',
  'cbam.goods.euExport': 'Of which exported to the EU',
  'cbam.goods.alParams': 'Aluminium sector parameters',
  'cbam.goods.scrap': 'Scrap per tonne of product',
  'cbam.goods.preConsumer': 'Pre-consumer scrap',
  'cbam.goods.nonAl': 'Non-aluminium elements',
  'cbam.goods.nonAlHint': 'Only needed when above 1% of the mass.',
  'cbam.goods.precursors': 'Precursors',
  'cbam.goods.precursorsSub': 'Input materials that are themselves CBAM goods (e.g. unwrought aluminium 7601, profiles 7604).',
  'cbam.goods.addPrecursor': 'Add precursor',
  'cbam.goods.saved': 'Good saved',
  'cbam.goods.deleted': 'Good deleted',
  'cbam.goods.saveFailed': 'Could not save. Try again.',
  'cbam.goods.saveCta': 'Save the good',
  'cbam.goods.vProcess': 'Pick the production process.',
  'cbam.goods.vCn': 'The CN code needs at least 6 digits.',
  'cbam.goods.vProduction': 'Annual production is required.',
  'cbam.goods.vPrec': 'Every precursor needs a name and a quantity (t/t) above zero.',
  'cbam.goods.vPrecCountry': 'Pick the country of production for every default-valued precursor — or "Unknown origin" when the country cannot be identified.',
  'cbam.goods.vPrecSource': 'Every "Produced in this installation" precursor needs a source good.',
  'cbam.goods.vPrecSelf': 'A good cannot be its own precursor — pick a different source good.',
  'cbam.goods.vPrecSameProcess': 'The source good must belong to a different production process — within one process the internal flow carries no SEE×mass term (Annex III, point B, IR (EU) 2025/2547).',
  'cbam.goods.yearClearedSources': 'Changing the reporting year cleared the "Produced in this installation" links — an internal precursor must reference a good of the same reporting year (Annex II point E(1), IR (EU) 2025/2547). Pick the source goods again.',
  'cbam.goods.precSyncFailed': 'The good was saved, but these precursor rows failed: {rows}. Press save again — the other rows went through.',
  'cbam.goods.confirmDelete': 'Delete {cn} ({year})? Its precursors go with it.',
  'cbam.prec.name': 'Name',
  'cbam.prec.namePh': 'e.g. Aluminium profiles',
  'cbam.prec.cn': 'CN code',
  'cbam.prec.tpt': 'Quantity per tonne of good',
  'cbam.prec.tptHint': 'Include losses — offcuts and scrap that never reach the final good still count.',
  'cbam.prec.euOrigin': 'EU / EEA / Swiss origin',
  'cbam.prec.euOriginHint': 'Counts as zero embedded emissions.',
  'cbam.prec.supplier': 'Supplier value',
  'cbam.prec.default': 'Default value',
  'cbam.prec.seeDirect': 'Direct SEE',
  'cbam.prec.seeIndirect': 'Indirect SEE',
  'cbam.prec.supplierName': 'Supplier',
  'cbam.prec.supplierCountry': 'Supplier country',
  'cbam.prec.period': 'Reporting period',
  'cbam.prec.periodHint': 'The production year the supplier\'s values cover, e.g. 2026.',
  'cbam.prec.originOperator': 'Origin operator',
  'cbam.prec.originInstallation': 'Origin installation',
  'cbam.prec.originRegistryId': 'CBAM registry ID',
  'cbam.prec.evidence': 'Evidence reference',
  'cbam.prec.evidenceHint': 'The supplier\'s verified report backing these values.',
  'cbam.prec.supplierHint': 'Supplier values only count when verified by an EU-accredited verifier — keep the document reference.',
  'cbam.prec.country': 'Country of production',
  'cbam.prec.unknownWarn': 'When the country of production cannot be identified, the punitive default applies — the highest value across all countries, per Annex IV of IR (EU) 2025/2621 (e.g. 7601 = 3.198 tCO₂e/t base). If you know the country, pick it.',
  'cbam.prec.remove': 'Remove',

  /* — "Produced in this installation", the third value source — */
  'cbam.prec.internal': 'Produced in this installation',
  'cbam.prec.internalShort': 'Produced here',
  'cbam.prec.sourceGood': 'Source good',
  'cbam.prec.internalCnHint': 'Taken from the source good.',
  'cbam.prec.tptInternalHint': 'Gross input mass — include offcuts and scrap that never reach the final good (Annex III, point B).',
  'cbam.prec.internalEvidenceHint': 'The internal production record backing the quantity.',
  'cbam.prec.noSourceGoods': 'No other good in {year} can serve as the source — create the source good first (in a different production process), save it, then link it here.',
  'cbam.prec.internalHint': 'Embedded emissions for this row are resolved at calculation time from the source good\'s own calculated SEE — direct and indirect separately — per Annex II point E(1) of IR (EU) 2025/2547, as part of the operator\'s monitoring and calculation duties under Regulation (EU) 2023/956.',

  /* — CN-lookup read-out (English in both dictionaries, like cbam.overview.*) — */
  'cbam.lookup.checking': 'Checking the CN code…',
  'cbam.lookup.inAnnex': 'Annex I good',
  'cbam.lookup.notInAnnex': 'CN {code} is not in Annex I — it is not a CBAM good and owes no certificates.',
  'cbam.lookup.indirectMemo': 'Only DIRECT embedded emissions owe certificates for this sector — indirect (electricity) emissions are reported as a memo.',
  'cbam.lookup.indirectCharged': 'Direct AND indirect embedded emissions are both chargeable for this sector.',
  'cbam.lookup.default': 'Default value {year}',
  'cbam.lookup.fallback': 'No country-specific default — the "Other countries" fallback applies',
  'cbam.lookup.benchmark': 'CBAM benchmark',

  // ── Legacy-page sweep (hardcoded Albanian → i18n), 2026-10-22 ──
  'regw.tokenMissing': 'The registration link is invalid. Please contact support.',
  'regw.tokenInvalid': 'The registration link is invalid.',
  'regw.tokenExpired': 'The registration link has expired. Please contact support for a new link.',
  'regw.tokenUsed': 'This registration link has already been used. If you need help, contact support.',
  'regw.tokenVerifyFailed': 'Error verifying the link. Please try again or contact support.',
  'regw.requiredField': 'This field is required',
  'regw.invalidEmail': 'Invalid email',
  'regw.passwordMinLength': 'The password must be at least 6 characters',
  'regw.passwordsMismatch': 'The passwords do not match',
  'regw.invalidYear': 'Invalid year',
  'regw.mustBePositive': 'Must be a positive number',
  'regw.mustBeWholeNumberMin': 'Must be a whole number >= 1',
  'regw.registrationSuccess': 'Registration completed successfully! You have been signed in automatically.',
  'regw.registrationFailed': 'Error during registration. Please try again.',
  'regw.verifyingLink': 'Verifying the registration link...',
  'regw.pleaseWait': 'Please wait...',
  'regw.invalidLinkTitle': 'Invalid link',
  'regw.contactSupportHelp': 'If you need help, please contact our support.',
  'regw.step1Title': 'Create your account',
  'regw.step1Subtitle': 'Fill in your account details to create your profile.',
  'regw.typeHere': 'Type here',
  'regw.passwordLabel': 'Password',
  'regw.confirmPasswordLabel': 'Confirm password',
  'regw.saveAndContinue': 'Save and continue',
  'regw.step2Title': 'Company',
  'regw.step2Subtitle': 'Fill in your company details to create your profile',
  'regw.companyNameLabel': 'Company name',
  'regw.addressLabel': 'Address',
  'regw.cityLabel': 'City',
  'regw.selectCity': 'Select a city',
  'regw.countryLabel': 'Country',
  'regw.selectCountry': 'Select a country',
  'regw.countryKosovo': 'Kosovo',
  'regw.countryAlbania': 'Albania',
  'regw.countryNorthMacedonia': 'North Macedonia',
  'regw.countryMontenegro': 'Montenegro',
  'regw.companyTypeLabel': 'Company type',
  'regw.selectType': 'Select a type',
  'regw.companySectorLabel': 'Company sector',
  'regw.selectSector': 'Select a sector',
  'regw.sectorAgriculture': 'Agriculture',
  'regw.sectorConstruction': 'Construction',
  'regw.sectorTechnology': 'Technology',
  'regw.sectorServices': 'Services',
  'regw.production': 'Production',
  'regw.sectorTrade': 'Trade',
  'regw.onsiteActivitiesLabel': 'On-site company activities',
  'regw.processing': 'Processing',
  'regw.assembly': 'Assembly',
  'regw.retail': 'Retail',
  'regw.wholesale': 'Wholesale',
  'regw.offsiteActivitiesLabel': 'Off-site company activities',
  'regw.distribution': 'Distribution',
  'regw.logistics': 'Logistics',
  'regw.maintenance': 'Maintenance',
  'regw.sustainabilityTargetLabel': 'Sustainability target',
  'regw.targetPlaceholder': 'Enter the target',
  'regw.usagePurposeLabel': 'Purpose of use',
  'regw.reportingTypeLabel': 'What type of reporting will you use it for',
  'regw.goBack': 'Go back',
  'regw.step3Title': 'Buildings',
  'regw.step3Subtitle': 'Register your building',
  'regw.nameLabel': 'Name',
  'regw.buildingLabel': 'Building',
  'regw.buildingIndustrial': 'Industrial building',
  'regw.buildingCommercial': 'Commercial building',
  'regw.buildingResidential': 'Residential building',
  'regw.buildingAdministrative': 'Administrative building',
  'regw.buildingMixed': 'Mixed-use building',
  'regw.constructionYearLabel': 'Year of construction',
  'regw.buildingSizeLabel': 'Building size',
  'regw.floorsLabel': 'Number of floors',
  'regw.primaryEnergyLabel': 'Primary energy source',
  'regw.selectPrimarySource': 'Select the primary source',
  'regw.energyGridElectricity': 'Grid electricity',
  'regw.energyNaturalGas': 'Natural gas',
  'regw.energyDistrictHeating': 'District heating (city network)',
  'regw.energyLpg': 'Liquefied gas (LPG – propane / butane)',
  'regw.energyHeatingOil': 'Heating oil / diesel',
  'regw.energyBiomass': 'Biomass (wood, pellets, etc.)',
  'regw.energyOnsiteRenewable': 'On-site renewable energy (solar, wind, geothermal)',
  'regw.energyMixed': 'Mixed energy sources',
  'regw.energyUnknown': 'Unknown',
  'regw.secondaryEnergyLabel': 'Secondary energy source (optional)',
  'regw.none': 'None',
  'regw.waterSupplyLabel': 'Water supply type',
  'regw.waterPublicNetwork': 'Public network',
  'regw.waterPrivateWell': 'Private well',
  'regw.waterNaturalSource': 'Natural source',
  'regw.finish': 'Finish',
  'admu.accessDenied': 'Access denied. Only administrators can access this page.',
  'admu.loadUsersFailed': 'Error loading users.',
  'admu.statusChanged': 'User status changed to: {status}',
  'admu.statusChangeFailed': 'Error changing the status.',
  'admu.resetPasswordFailed': 'Error resetting the password.',
  'admu.planChangedArchived': 'Plan changed to {plan}. {count} buildings were archived.',
  'admu.planChanged': 'User plan changed to: {plan}',
  'admu.planChangeFailed': 'Error changing the plan.',
  'admu.loadBuildingsFailed': 'Error loading buildings.',
  'admu.selectExactly': 'You must select exactly {count} building(s) to continue.',
  'admu.loadBuildingDetailsFailed': 'Error loading building details.',
  'admu.buildingRestored': 'Building restored successfully.',
  'admu.restoreBuildingFailed': 'Error restoring the building.',
  'admu.buildingNameEmpty': 'The building name cannot be empty.',
  'admu.nameSaved': 'Name saved successfully.',
  'admu.saveNameFailed': 'Error saving the name.',
  'admu.buildingArchived': 'Building archived successfully.',
  'admu.archiveBuildingFailed': 'Error archiving the building.',
  'admu.buildingNumber': 'Building #{id}',
  'admu.userDeleted': 'User {username} was deleted successfully.',
  'admu.deleteUserFailed': 'Error deleting the user.',
  'admu.backToApp': 'Back to the app',
  'admu.title': 'Users',
  'admu.subtitle': 'Manage user accounts and subscription plans',
  'admu.searchPlaceholder': 'Search by name, email, or username...',
  'admu.clearSearch': 'Clear search',
  'admu.resultCount': '{count} result',
  'admu.resultsCount': '{count} results',
  'admu.user': 'User',
  'admu.contact': 'Contact',
  'admu.status': 'Status',
  'admu.plan': 'Plan',
  'admu.buildings': 'Buildings',
  'admu.created': 'Created',
  'admu.actions': 'Actions',
  'admu.noSearchResults': 'No results found for your search',
  'admu.noUsers': 'No users found',
  'admu.archivedAbbrev': '+{count} arch.',
  'admu.disableUserTitle': 'Disable user',
  'admu.disable': 'Disable',
  'admu.enableUserTitle': 'Enable user',
  'admu.enable': 'Enable',
  'admu.setTempPasswordTitle': 'Set a temporary password',
  'admu.passwordButton': 'Password',
  'admu.deleteUserTitle': 'Delete user',
  'admu.delete': 'Delete',
  'admu.pageOf': 'Page {current} of {total}',
  'admu.tempPasswordTitle': 'Temporary password',
  'admu.tempPasswordSendTo': 'Send these details to',
  'admu.tempPasswordShownOnce': 'This password is shown only now and cannot be read again later.',
  'admu.passwordLabel': 'Password',
  'admu.clientMessage': 'Hello,\n\nSign in to Karbon360° with these details:\n\nAddress: https://app.karbon360.tech\nUsername: {username}\nPassword: {password}\n\nPlease change your password after your first login.\n',
  'admu.copyClientMessage': 'Copy message for the client',
  'admu.close': 'Close',
  'admu.deleteUserHeading': 'Delete User',
  'admu.disableUserHeading': 'Disable User',
  'admu.enableUserHeading': 'Enable User',
  'admu.confirmDeletePre': 'Are you sure you want to delete the user',
  'admu.cannotUndo': 'This action cannot be undone.',
  'admu.confirmDisablePre': 'Are you sure you want to disable the user',
  'admu.confirmEnablePre': 'Are you sure you want to enable the user',
  'admu.noCancel': 'No, Cancel',
  'admu.yesContinue': 'Yes, Continue',
  'admu.loading': 'Loading...',
  'admu.activeBuildings': 'Active Buildings',
  'admu.noActiveBuildings': 'No active buildings',
  'admu.buildingNamePlaceholder': 'Building name',
  'admu.save': 'Save',
  'admu.cancel': 'Cancel',
  'admu.invoiceCount': '{count} invoices',
  'admu.emissionCount': '{count} emissions',
  'admu.archive': 'Archive',
  'admu.archivedBuildings': 'Archived Buildings',
  'admu.noArchivedBuildings': 'No archived buildings',
  'admu.archivedOn': 'Archived: {date}',
  'admu.planLimitReached': 'Plan limit reached. Upgrade the plan to restore.',
  'admu.restoreBuilding': 'Restore building',
  'admu.chooseBuildingsTitle': 'Choose Buildings to Keep',
  'admu.dgHas': 'has',
  'admu.dgActiveButPlan': 'active buildings, but the plan',
  'admu.dgAllowsOnly': 'allows only',
  'admu.downgradeWarning': 'Select {count} building(s) to keep. The other buildings will be archived (not deleted).',
  'admu.loadingBuildings': 'Loading buildings...',
  'admu.selected': 'Selected:',
  'admu.confirmChange': 'Confirm Change',
  'admt.sessionExpired': 'Your session has expired. Please sign in again.',
  'admt.noAdminAccess': 'This account does not have administrative access.',
  'admt.loadFailed': 'Error loading tokens.',
  'admt.inviteSent': 'Invitation sent to {email}',
  'admt.tokenGenerated': 'New token generated successfully!',
  'admt.generateFailed': 'Error generating the token.',
  'admt.copied': 'Copied to clipboard!',
  'admt.used': 'Used',
  'admt.expired': 'Expired',
  'admt.active': 'Active',
  'admt.statActive': 'Active',
  'admt.pageTitle': 'Token Management',
  'admt.pageSubtitle': 'Generate and manage registration tokens for new users.',
  'admt.backToUsers': 'Back to Users',
  'admt.statTotal': 'Total Tokens',
  'admt.generateNewToken': 'Generate New Token',
  'admt.inviteEmailLabel': 'Invitee email',
  'admt.inviteEmailPlaceholder': 'name@company.com',
  'admt.inviteNameLabel': 'Name (optional)',
  'admt.inviteNamePlaceholder': 'Contact name',
  'admt.planLabel': 'Plan',
  'admt.planBasic': 'Basic · 1 building',
  'admt.planAdvanced': 'Advanced · 3 buildings',
  'admt.planCorporate': 'Corporate · 100 buildings',
  'admt.expiresInDaysLabel': 'Expires in (days)',
  'admt.generateWithEmailTitle': 'Generates the token and sends the invitation by email',
  'admt.generateOnlyTitle': 'Generates the token only (no email)',
  'admt.sending': 'Sending...',
  'admt.sendInvite': 'Send invitation',
  'admt.generateToken': 'Generate Token',
  'admt.newTokenHeading': 'New Token Generated',
  'admt.inviteSentToPrefix': 'Invitation sent to',
  'admt.inviteSentNoManual': 'You do not need to send the link manually.',
  'admt.invite': 'The invitation',
  'admt.notSent': 'was not sent',
  'admt.copyLinkManually': 'Copy the link below and send it manually.',
  'admt.registrationLinkLabel': 'Registration Link:',
  'admt.copy': 'Copy',
  'admt.expiresOn': 'Expires on:',
  'admt.existingTokens': 'Existing Tokens',
  'admt.tokenCount': '{count} tokens',
  'admt.emptyTitle': 'No tokens generated yet.',
  'admt.emptyHint': 'Generate a new token to get started.',
  'admt.thInviteFor': 'Invited',
  'admt.thStatus': 'Status',
  'admt.thCreatedAt': 'Created',
  'admt.thExpiresAt': 'Expires',
  'admt.thUsedAt': 'Used',
  'bldp.pageTitle': 'Buildings',
  'bldp.registrationTitle': 'BUILDING REGISTRATION',
  'bldp.addBuilding': 'Add building',
  'bldp.name': 'Name',
  'bldp.building': 'Building',
  'bldp.address': 'Address',
  'bldp.city': 'City',
  'bldp.country': 'Country',
  'bldp.constructionYear': 'Year built',
  'bldp.areaSqm': 'Building size',
  'bldp.floors': 'Number of floors',
  'bldp.energySources': 'Energy sources in the building',
  'bldp.waterSupplyType': 'Water supply type',
  'bldp.heatingCoolingType': 'Heating and cooling type',
  'bldp.selectType': 'Select type',
  'bldp.selectSource': 'Select source',
  'bldp.typeCommercial': 'Commercial building',
  'bldp.typeIndustrial': 'Industrial building',
  'bldp.typeResidential': 'Residential building',
  'bldp.typeOffice': 'Office',
  'bldp.energyGrid': 'Electric grid',
  'bldp.energyNaturalGas': 'Natural gas',
  'bldp.energyOil': 'Oil',
  'bldp.energySolar': 'Solar energy',
  'bldp.waterPublic': 'Public network',
  'bldp.waterWell': 'Well',
  'bldp.waterIndependent': 'Independent system',
  'bldp.heatingCentral': 'Central',
  'bldp.heatingIndividual': 'Individual',
  'bldp.heatingNone': 'None',
  'bldp.saving': 'Saving…',
  'bldp.addBuildingSubmit': 'Add building',
  'bldp.yourBuildings': 'Your buildings',
  'bldp.edit': 'Edit',
  'bldp.delete': 'Delete',
  'bldp.nameRequired': 'Name is required',
  'bldp.buildingTypeRequired': 'Building type is required',
  'bldp.addressRequired': 'Address is required',
  'bldp.cityRequired': 'City is required',
  'bldp.countryRequired': 'Country is required',
  'bldp.yearRequired': 'Year is required',
  'bldp.areaRequired': 'Size is required',
  'bldp.floorsRequired': 'Number of floors is required',
  'bldp.energyRequired': 'Energy source is required',
  'bldp.waterRequired': 'Water supply is required',
  'bldp.heatingRequired': 'Heating/cooling is required',
  'bldp.loadFailed': 'Failed to load buildings.',
  'bldp.planLimit': 'You have reached the building limit for your plan.',
  'bldp.addFailed': 'Failed to add the building.',
  'bldp.updateFailed': 'Failed to update the building.',
  'bldp.deleteFailed': 'Failed to delete the building.',
  'facp.addFacility': 'Add facility',
  'facp.nameRequired': 'Facility name is required.',
  'facp.updated': 'Facility updated',
  'facp.added': 'Facility added',
  'facp.saveFailed': 'Could not save. Please try again.',
  'facp.deleted': 'Facility deleted',
  'facp.deleteFailed': 'Could not delete',
  'facp.delete': 'Delete',
  'facp.edit': 'Edit',
  'facp.addData': 'Add data',
  'facp.editFacility': 'Edit facility',
  'facp.cancel': 'Cancel',
  'facp.saving': 'Saving…',
  'facp.saveChanges': 'Save changes',
  'facp.addFacilitySubmit': 'Add facility',
  'facp.name': 'Name',
  'facp.namePlaceholder': 'e.g. Head office',
  'facp.buildingType': 'Building type',
  'facp.selectType': 'Select type',
  'facp.address': 'Address',
  'facp.city': 'City',
  'facp.country': 'Country',
  'facp.constructionYear': 'Year built',
  'facp.areaSqm': 'Building size',
  'facp.areaHint': 'Used for intensity — without it, intensity cannot be calculated.',
  'facp.floors': 'Number of floors',
  'facp.energySources': 'Energy sources',
  'facp.energyPlaceholder': 'e.g. Electricity, gas',
  'facp.heatingCooling': 'Heating and cooling',
  'facp.waterSupply': 'Water supply',
  'facp.typeOffice': 'Office',
  'facp.typeWarehouse': 'Warehouse',
  'facp.typeProduction': 'Production',
  'facp.typeRetail': 'Retail',
  'facp.typeDataCenter': 'Data center',
  'facp.typeOther': 'Other',
  's3p.modeWalk': 'Walking',
  's3p.modeBike': 'Bicycle',
  's3p.modeBus': 'Bus',
  's3p.modeRail': 'Rail/Tram',
  's3p.modeCarSolo': 'Car (Solo)',
  's3p.modeCarCarpool': 'Car (Carpool)',
  's3p.enterEmployees': 'Please enter the total number of employees',
  's3p.fillOneMode': 'Please fill in at least one travel mode',
  's3p.error': 'Error: {message}',
  's3p.confirmBuilding': 'Please confirm the building before saving the data',
  's3p.commuteSaved': 'Saved {created} records{dupes} · Employee commuting · {period}.',
  's3p.commuteSavedDupes': '; {skipped} skipped as duplicates',
  's3p.saved': '{value} t CO₂e added to Scope 3 · {period}.',
  's3p.saveFailedCheckBuilding': 'Saving failed. Check the building and try again.',
  'mflow.title': 'Monthly data entry',
  'mflow.stepStationary': 'Stationary combustion',
  'mflow.stepMobile': 'Mobile combustion',
  'mflow.stepProcess': 'Process emissions',
  'mflow.stepElectricity': 'Electricity',
  'mflow.stepTravel': 'Travel (business + commuting)',
  'mflow.heading': 'Data entry',
  'mflow.headingSub': 'Go through the scopes step by step — the monthly report fills itself in.',
  'mflow.prevMonth': 'Previous month',
  'mflow.nextMonth': 'Next month',
  'mflow.stepsDone': '{done} / {total} steps with data',
  'mflow.monthComplete': 'Month complete ✓',
  'mflow.summaryTitle': 'Summary for {month}',
  'mflow.summarySub': 'All three scopes have data — the month enters the report complete.',
  'mflow.total': 'Total',
  'mflow.biogenicMemo': 'Biogenic CO₂ (memo): {value} t — reported separately, outside the total.',
  'mflow.viewReports': 'View reports',
  'mflow.viewAllInvoices': 'View all invoices',
  'mflow.loading': 'Loading…',
  'mflow.ifApplicable': 'if applicable',
  'mflow.recordsOne': '{count} record · {total} t CO₂e',
  'mflow.recordsMany': '{count} records · {total} t CO₂e',
  'mflow.noData': 'No data for this month',
  'mflow.continueHere': 'Continue here →',
  'mflow.addReview': 'Add / review',
  'mflow.addData': 'Add data →',
  'mflow.footerLead': 'This screen tracks only the selected month\'s data entry. The full picture is under',
  'mflow.detailedReport': 'Detailed report',
  'mflow.and': 'and',
  's1fug.title': 'Scope 1 - Fugitive Emissions',
  's1fug.fillAllFields': 'Fill in all fields before calculating',
  's1fug.duplicate': 'This record already exists for this period',
  's1fug.saveError': 'Error while saving: {message}',
  's1fug.building': 'Building',
  's1fug.selectBuilding': 'Select a building',
  's1fug.period': 'Period',
  's1fug.source': 'Source of fugitive emissions',
  's1fug.selectSource': 'Select the source',
  's1fug.gasType': 'Gas / refrigerant type',
  's1fug.selectType': 'Select the type',
  's1fug.quantity': 'Quantity lost / emitted (kg)',
  's1fug.qtyPlaceholder': 'e.g. 2.5',
  's1fug.calculating': 'Calculating...',
  's1fug.calculate': 'Calculate',
  's1fug.saving': 'Saving...',
  's1fug.savedBadge': '✓ Saved',
  's1fug.save': 'Save',
  's1fug.clear': 'Clear',
  's1fug.result': 'Calculation result',
  's1fug.gasRefrigerant': 'Gas / Refrigerant',
  's1fug.emissions': 'Emissions',
  's1dj.sessionExpired': 'Your session has expired. Please sign in again.',
  's1dj.saveFailed': 'Saving failed. Try again.',
  's1dj.liters': 'Liters',
  's1dj.biogenicSeparate': 'Biogenic CO₂ ({period}): {value} t CO₂e — reported separately, not in Scope 1',
  'invp.pickBuildingBeforeInvoice': 'Please select a building before uploading the invoice',
  'invp.pickBuildingBeforeInvoices': 'Please select a building before uploading the invoices',
  'invp.pickBuilding': 'Please select a building',
  'invp.sessionExpired': 'Your session has expired. Please sign in again.',
  'invp.saveError': 'Error while saving: {message}',
  'invp.saveRetry': 'Saving failed. Please try again.',
  'invnp.detailsLoadFailed': 'Failed to load the invoice details',
  'invnp.allTab': 'ALL',
  'invnp.thisInvoice': 'this invoice',
  'invnp.confirmDelete': 'Delete "{label}"? This action cannot be undone.',
  'invnp.deleteFailed': 'Deletion failed',
  'invnp.errorWithMessage': 'Error: {message}',
  'invnp.colSource': 'Source',
  'invnp.scanned': 'Scanned',
  'invnp.colBuilding': 'Building',
  'invnp.colEmissions': 'CO₂ emissions',
  'invnp.tabStationary': 'Stationary combustion',
  'invnp.tabMobile': 'Mobile combustion',
  'invnp.tabProcess': 'Process emissions',
  'invnp.tabElectricity': 'Electricity',
  'invnp.pageTitle': 'Invoice register',
  's1mob.pickBuildingFirst': 'Please select a building before continuing',
  's1mob.litres': 'Litres',
  'recp.title': 'Invoices',
  'recp.allCategories': 'All categories',
  'recp.catScope1Electricity': 'Scope 1 - Electricity',
  'recp.catScope1Mobile': 'Scope 1 - Mobile combustion',
  'recp.catScope1Stationary': 'Scope 1 - Stationary combustion',
  'recp.catScope1Heating': 'Scope 1 - Heating',
  'recp.catScope1Ev': 'Scope 1 - Electric vehicles',
  'recp.catScope2Electricity': 'Scope 2 - Electricity',
  'recp.catScope3Travel': 'Scope 3 - Business travel',
  'recp.listTitle': 'Invoice list',
  'recp.colSelect': 'Select',
  'recp.colName': 'Name',
  'recp.colDate': 'Date',
  'recp.colView': 'View',
  'recp.colDelete': 'Delete',
  'recnp.tabScope1Stationary': 'Scope 1 - Stationary combustion',
  'recnp.tabScope1Mobile': 'Scope 1 - Mobile combustion',
  'recnp.tabScope1Process': 'Scope 1 - Process emissions',
  'recnp.pageTitle': 'Recipes',
  'repp.pageTitle': 'Detailed report',
  'repp.colLabel': 'Label',
  'repp.colRecords': 'Records',
  'repp.colQuantity': 'Quantity',
  'repp.colEmissions': 'Emissions (tCO₂e)',
  'repp.colSubtotalPct': '% of subtotal',
  'repp.colScopePct': '% of scope',
  'repp.periodLabel': 'Period:',
  'repp.buildingLabel': 'Building:',
  'repp.allBuildings': 'All',
  'repp.scope1Title': 'Scope 1 — Direct emissions',
  'repp.stationaryByFuel': 'Stationary combustion — by fuel',
  'repp.mobileByVehicle': 'Mobile combustion — by vehicle',
  'repp.processByType': 'Processes — by type',
  'repp.scope2Title': 'Scope 2 — Indirect emissions (energy)',
  'repp.byConsumptionType': 'By consumption type',
  'repp.scope3Title': 'Scope 3 — Indirect emissions (value chain)',
  'repp.byActivity': '{label} — by activity',
  'repp.biogenicNote': 'Reported for transparency, not included in Scope 1 — per the GHG Protocol & ISO 14064.',
  'repp.byBiogenicFuel': 'By biogenic fuel',
  'dashp.logoUploadFailed': 'Logo upload failed',
  'dashp.logoRemoveFailed': 'Logo removal failed',
  'dashp.scope2Elec': 'Scope 2 (electricity)',
  'dashp.and': 'and',
  'dashp.stationary': 'Stationary combustion',
  'dashp.mobile': 'Mobile combustion',
  'dashp.electricity': 'Electricity',
  'dashp.heating': 'Heating',
  'dashp.evCharging': 'Electric vehicles',
  'dashp.stationaryByFuel': 'Stationary combustion — by fuel',
  'dashp.mobileByVehicle': 'Mobile combustion — by vehicle',
  'dashp.processByType': 'Processes — by type',
  'dashp.scope2ByType': 'Scope 2 — by type',
  'invdet.fileGone': 'The original document is no longer available on the server.',
  'invdet.previewFailed': 'We could not load the document.',
  'invdet.typeLabel': 'TYPE:',
  'invdet.vehicle': 'Vehicle',
  'invdet.methodLabelColon': 'METHOD:',
  'invdet.methodLabel': 'METHOD',
  'invdet.fuelDetails': 'Fuel details',
  'invdet.fuelType': 'Fuel type',
  'invdet.quantity': 'Quantity',
  'invdet.period': 'Period',
  'invdet.emissions': 'Emissions',
  'invdet.supplierLabel': 'SUPPLIER:',
  'invdet.registers': 'Registers',
  'invdet.comparison': 'Comparison',
  'invdet.prevMonth': 'Previous month',
  'invdet.noDataLine1': 'No',
  'invdet.noDataLine2': 'data',
  'invdet.currMonth': 'Current month',
  'invdet.electricityUse': 'Electricity consumption',
  'invdet.carbonEmissions': 'Carbon emissions',
  'invdet.loadingDoc': 'Loading document...',
  'invdet.noAttachment': 'No document attached',
  'invdet.categoryLabel': 'CATEGORY:',
  'invdet.activityDetails': 'Activity details',
  'invdet.sourceOcr': 'This record was created automatically from the invoice scan',
  'invdet.sourceManual': 'This record was added manually',
  'eflow.verifyTitle': 'Verify the calculation',
  'eflow.savedTitle': 'Activity recorded',
  'eflow.closeNoSave': 'Close without saving',
  'eflow.close': 'Close',
  'eflow.attachedDoc': 'Attached document',
  'eflow.document': 'Document',
  'eflow.scannedDoc': 'Scanned document',
  'eflow.saving': 'Saving…',
  'eflow.save': 'Save',
  'eflow.backAndFix': 'Go back & correct',
  'eflow.savedSuccess': 'Saved successfully',
  'eflow.monthCoverage': 'Coverage for the month',
  'eflow.scopeComplete': 'This scope is complete for the month ✓',
  'eflow.continueTo': 'Continue to {label} →',
  'eflow.addAnother': 'Add a new activity',
  'eflow.finish': 'Finish',
  'dtbl.noData': 'No data',
  'dtbl.delete': 'Delete',
  'tabf.all': 'All',
  'appx.sessionExpired': 'Your session has expired. Please sign in again.',
  'appx.resetLinkInvalid': 'The reset link is invalid.',
  'appx.resetLinkExpired': 'The reset link has expired or has already been used.',
  'appx.resetLinkVerifyError': 'Error verifying the link.',
  'appx.passwordMin': 'The password must be at least 6 characters.',
  'appx.passwordMismatch': 'The passwords do not match.',
  'appx.passwordChanged': 'Your password was changed successfully. Please sign in again.',
  'appx.resetFailed': 'Password reset failed.',
  'appx.resetTitle': 'Reset password',
  'appx.validatingLink': 'Verifying the link...',
  'appx.newPassword': 'New password',
  'appx.confirmPassword': 'Confirm password',
  'appx.savePassword': 'Save password',
  'appx.backToLogin': 'Back to log in',
  'purpose.spaceHeating': 'Space heating',
  'purpose.hotWater': 'Sanitary hot water',
  'purpose.processHeat': 'Process heat',
  'purpose.backupGenerator': 'Backup generator',
};

export default en;
